import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, PencilSimple, CheckCircle, XCircle,
  SpeakerHigh, ArrowLeft, ArrowRight, CopySimple, UserCircle,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { cn } from "@/lib/utils";
import { api } from "../api";

const STATUS_VARIANT = {
  approved: "success", draft: "neutral", review: "warning",
  rejected: "primary", archived: "neutral",
};
const DIFF_VARIANT = { easy: "success", medium: "warning", hard: "primary" };

/* ── Speak button ───────────────────────────────────────── */
function speakWord(text) {
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-GB"; u.rate = 0.85;
    window.speechSynthesis.speak(u);
  } catch { /* no-op */ }
}

/* ── Inline image lightbox ──────────────────────────────── */
function ImageLightbox({ word, onClose }) {
  const [slide, setSlide] = useState(0);
  const images = [
    word.image_url         ? { url: word.image_url,         label: word.image_label,         bg: "bg-teal"        } : null,
    word.antonym_image_url ? { url: word.antonym_image_url, label: word.antonym_image_label, bg: "bg-destructive" } : null,
  ].filter(Boolean);

  if (!images.length) return null;
  const cur = images[slide];

  return (
    <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/88 backdrop-blur-sm"
      onClick={onClose}>
      <button onClick={onClose} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25">
        <X size={18} weight="bold" />
      </button>
      <div className="flex flex-col items-center px-6" style={{ maxWidth: "380px", width: "100%" }}
        onClick={(e) => e.stopPropagation()}>
        {cur.label && (
          <div className={cn("mb-2 w-full rounded-t-xl py-1.5 text-center", cur.bg)}>
            <span className="text-xs font-extrabold uppercase tracking-wide text-white">{cur.label}</span>
          </div>
        )}
        <img src={cur.url} alt="" draggable={false}
          className={cn("w-full rounded-b-xl object-contain shadow-2xl", !cur.label && "rounded-t-xl")}
          style={{ maxHeight: "70svh" }} />
      </div>
      {images.length > 1 && (
        <div className="mt-4 flex items-center gap-4">
          <button onClick={(e) => { e.stopPropagation(); setSlide(0); }}
            className={cn("h-2 rounded-full transition-all", slide === 0 ? "w-5 bg-white" : "w-2 bg-white/40")} />
          <button onClick={(e) => { e.stopPropagation(); setSlide(1); }}
            className={cn("h-2 rounded-full transition-all", slide === 1 ? "w-5 bg-white" : "w-2 bg-white/40")} />
        </div>
      )}
    </div>
  );
}

/* ── VS image panel ─────────────────────────────────────── */
function ImagePanel({ word, onTap }) {
  const hasWord    = !!word.image_url;
  const hasAntonym = !!word.antonym_image_url;
  if (!hasWord && !hasAntonym) return null;

  const wordBottom    = word.image_caption    || word.synonyms?.slice(0, 3).join("  ·  ") || null;
  const antonymBottom = word.antonym_image_caption || word.antonyms?.slice(0, 3).join("  ·  ") || null;

  if (hasWord && hasAntonym) {
    return (
      <div className="relative mt-5 overflow-hidden rounded-2xl shadow-soft cursor-pointer" onClick={onTap}>
        <div className="flex items-stretch">
          {/* Word */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex min-h-[28px] items-center justify-center bg-teal px-2 py-1">
              {word.image_label && <span className="text-[10px] font-extrabold uppercase tracking-wide text-white">{word.image_label}</span>}
            </div>
            <img src={word.image_url} alt={word.word} draggable={false}
              className="w-full object-cover" style={{ aspectRatio: "3/4" }} />
            <div className="flex min-h-[26px] items-center justify-center bg-teal/90 px-2 py-1">
              {wordBottom && <span className="text-center text-[9px] font-semibold text-white">{wordBottom}</span>}
            </div>
          </div>
          {/* Antonym */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex min-h-[28px] items-center justify-center bg-destructive px-2 py-1">
              {word.antonym_image_label && <span className="text-[10px] font-extrabold uppercase tracking-wide text-white">{word.antonym_image_label}</span>}
            </div>
            <img src={word.antonym_image_url} alt={word.antonyms?.[0]} draggable={false}
              className="w-full object-cover" style={{ aspectRatio: "3/4" }} />
            <div className="flex min-h-[26px] items-center justify-center bg-destructive/90 px-2 py-1">
              {antonymBottom && <span className="text-center text-[9px] font-semibold text-white">{antonymBottom}</span>}
            </div>
          </div>
        </div>
        {/* VS badge */}
        <div className="absolute left-1/2 top-[calc(50%+1px)] z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-white font-display text-[11px] font-black text-foreground shadow-soft">VS</span>
        </div>
      </div>
    );
  }

  // Single image
  const url     = hasWord ? word.image_url         : word.antonym_image_url;
  const label   = hasWord ? word.image_label        : word.antonym_image_label;
  const caption = hasWord ? (word.image_caption || word.synonyms?.slice(0, 3).join("  ·  "))
                           : (word.antonym_image_caption || word.antonyms?.slice(0, 3).join("  ·  "));
  const bg      = hasWord ? "bg-teal" : "bg-destructive";

  return (
    <div className="mt-5 cursor-pointer overflow-hidden rounded-2xl shadow-soft" onClick={onTap}>
      {label && (
        <div className={cn("flex min-h-[28px] items-center justify-center px-2 py-1", bg)}>
          <span className="text-[10px] font-extrabold uppercase tracking-wide text-white">{label}</span>
        </div>
      )}
      <img src={url} alt="" draggable={false} className="w-full object-cover" style={{ aspectRatio: "3/4" }} />
      {caption && (
        <div className={cn("flex min-h-[26px] items-center justify-center px-2 py-1", `${bg}/90`)}>
          <span className="text-center text-[9px] font-semibold text-white">{caption}</span>
        </div>
      )}
    </div>
  );
}

/* ── Main WordPreview ────────────────────────────────────── */
export function WordPreview({ word, onClose, onEdit, onSaved, onAddSimilar }) {
  const [lightbox, setLightbox] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  const quickSave = async (status) => {
    setSaving(true); setError("");
    try {
      await api(`/admin/words/${word.word_id}`, { method: "PATCH", body: { status } });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-end bg-foreground/20 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-card shadow-soft-lg"
        data-testid="word-preview"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2.5">
            <BloomBadge variant={STATUS_VARIANT[word.status]} className="px-2.5 py-1 text-xs capitalize">
              {word.status}
            </BloomBadge>
            <span className="text-xs font-semibold text-muted-foreground">{word.source}</span>
          </div>
          <div className="flex items-center gap-2">
            <BloomButton size="sm" leftIcon={PencilSimple} onClick={onEdit} variant="outline">
              Edit
            </BloomButton>
            <button onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-border">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* Scrollable word card */}
        <div className="flex-1 overflow-y-auto px-5 py-5">

          {/* Word title */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-4xl font-black capitalize tracking-tight text-foreground">
              {word.word}
            </h1>
            <button onClick={() => speakWord(word.word)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow transition active:scale-90">
              <SpeakerHigh size={20} weight="fill" />
            </button>
            <span className="flex flex-wrap gap-1.5">
              {word.word_type  && <BloomBadge variant="neutral" className="px-2.5 py-1 text-xs">{word.word_type}</BloomBadge>}
              {word.difficulty && <BloomBadge variant={DIFF_VARIANT[word.difficulty] || "neutral"} className="px-2.5 py-1 text-xs">{word.difficulty}</BloomBadge>}
              {word.years?.length > 0 && <BloomBadge variant="neutral" className="px-2.5 py-1 text-xs">Y{word.years.join(", Y")}</BloomBadge>}
              {word.gs_11plus  && <BloomBadge variant="blue" className="px-2.5 py-1 text-xs">11+</BloomBadge>}
            </span>
          </div>

          {/* Requested by — shown for parent_request words */}
          {word.source === "parent_request" && (word.requested_by_name || word.requested_by_email) && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl bg-primary/8 px-4 py-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary-strong">
                <UserCircle size={17} weight="fill" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">Requested by parent</p>
                {word.requested_by_name && (
                  <p className="text-sm font-extrabold text-foreground leading-tight">{word.requested_by_name}</p>
                )}
                {word.requested_by_email && (
                  <p className="truncate text-xs font-semibold text-muted-foreground">{word.requested_by_email}</p>
                )}
              </div>
            </div>
          )}

          {/* Meaning */}
          {word.meaning ? (
            <p className="mt-4 text-base font-semibold leading-relaxed text-foreground/90">{word.meaning}</p>
          ) : (
            <p className="mt-4 rounded-2xl bg-warning/10 px-4 py-3 text-sm font-semibold text-warning-ink">
              No meaning set yet
            </p>
          )}

          {/* Synonyms */}
          {word.synonyms?.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Synonyms</p>
              <p className="mb-2 text-[10px] font-semibold text-muted-foreground/70">Words with a similar meaning</p>
              <div className="flex flex-wrap gap-2">
                {word.synonyms.map((s) => (
                  <span key={s} className="rounded-full bg-teal/15 px-3.5 py-2 text-sm font-extrabold text-teal-ink">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Antonyms */}
          {word.antonyms?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Antonyms</p>
              <p className="mb-2 text-[10px] font-semibold text-muted-foreground/70">Words with the opposite meaning</p>
              <div className="flex flex-wrap gap-2">
                {word.antonyms.map((a) => (
                  <span key={a} className="rounded-full bg-destructive/10 px-3.5 py-2 text-sm font-extrabold text-destructive">{a}</span>
                ))}
              </div>
            </div>
          )}

          {/* Images */}
          <ImagePanel word={word} onTap={() => setLightbox(true)} />

          {/* Sentences */}
          {word.sentences?.length > 0 && (
            <div className="mt-5 space-y-2">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">In a sentence</p>
              {word.sentences.map((s, i) => (
                <p key={i} className="rounded-2xl bg-muted/60 px-4 py-3 text-sm font-semibold text-foreground/85">{s}</p>
              ))}
            </div>
          )}

          {/* Tags */}
          {word.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {word.tags.map((t) => (
                <span key={t} className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-bold text-muted-foreground">{t}</span>
              ))}
            </div>
          )}

          {error && <p className="mt-3 text-sm font-bold text-destructive">{error}</p>}
        </div>

        {/* Quick actions footer */}
        <div className="flex flex-wrap gap-2 border-t border-border px-5 py-4">
          {word.status !== "approved" && (
            <BloomButton size="sm" variant="teal" leftIcon={CheckCircle}
              disabled={saving} onClick={() => quickSave("approved")}>
              Approve
            </BloomButton>
          )}
          {word.status === "approved" && (
            <BloomButton size="sm" variant="outline" disabled={saving}
              onClick={() => quickSave("draft")}>
              Move to draft
            </BloomButton>
          )}
          {word.status !== "rejected" && (
            <BloomButton size="sm" variant="ghost" leftIcon={XCircle}
              disabled={saving} onClick={() => quickSave("rejected")}
              className="text-destructive">
              Reject
            </BloomButton>
          )}
          {/* Add similar word — copies image + metadata, leaves word/meaning/sentences blank */}
          <BloomButton
            size="sm"
            variant="outline"
            leftIcon={CopySimple}
            onClick={() => onAddSimilar?.(word)}
            className="ml-auto"
          >
            Add similar word
          </BloomButton>
        </div>
      </motion.div>

      {/* Image lightbox */}
      <AnimatePresence>
        {lightbox && <ImageLightbox word={word} onClose={() => setLightbox(false)} />}
      </AnimatePresence>
    </>
  );
}
